/**
 * Secure Token Storage Service
 * 
 * Provides secure token storage using httpOnly cookies instead of localStorage/sessionStorage
 * to protect against XSS attacks. Includes automatic token refresh and secure transmission.
 * 
 * When running in standalone mode (outside Evo tabs), the identity token provider is used
 * to obtain OIDC access tokens from Access Identity via @workspace/identity-react.
 */

import { getLoginRedirectUrl } from './config';
import { getIdentityToken, hasTokenProvider } from './identityTokenProvider';
import { isRunningStandalone } from '../utils/evoDetection';

export interface TokenInfo {
  token: string;
  expiresAt: number;
  refreshToken?: string;
}

export interface SecureTokenStorage {
  getToken(): Promise<string | null>;
  setToken(tokenInfo: TokenInfo): Promise<void>;
  clearToken(): Promise<void>;
  isTokenExpired(): Promise<boolean>;
  refreshToken(): Promise<string | null>;
}

/**
 * Cookie-based secure token storage implementation
 * Uses httpOnly cookies set by the backend for maximum security
 */
export class CookieTokenStorage implements SecureTokenStorage {
  private readonly tokenEndpoint: string;
  private readonly refreshEndpoint: string;

  constructor(baseUrl: string) {
    this.tokenEndpoint = `${baseUrl}/auth/token`;
    this.refreshEndpoint = `${baseUrl}/auth/refresh`;
  }

  /**
   * Get the current access token
   * 
   * When running in standalone mode (outside Evo tabs), uses the identity token provider
   * to get OIDC access tokens from Access Identity via @workspace/identity-react.
   * In standalone mode, we do NOT fall back to /api/auth/token as the OIDC flow
   * handles authentication entirely client-side.
   * 
   * When running inside an Evo tab, uses the cookie-based /api/auth/token endpoint.
   */
  async getToken(): Promise<string | null> {
    const standalone = isRunningStandalone();
    
    // In standalone mode, use identity tokens exclusively
    if (standalone) {
      // If the identity provider is not yet set up, return null
      // This allows the UI to show a loading state while identity initialises
      if (!hasTokenProvider()) {
        return null;
      }
      
      try {
        const identityToken = await getIdentityToken();
        if (identityToken) {
          return identityToken;
        }
        // Identity provider is set but returned no token - user needs to authenticate
        return null;
      } catch (error) {
        console.warn('Failed to get identity token:', error);
        return null;
      }
    }
    
    // Cookie-based authentication via /api/auth/token
    // Used only for Evo tab mode (not standalone)
    try {
      const response = await fetch(this.tokenEndpoint, {
        method: 'GET',
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, try to refresh
          const refreshedToken = await this.refreshToken();
          // Don't redirect here - let the UI components handle authentication state
          return refreshedToken;
        } else if (response.status === 500) {
          // Server error - don't try to refresh, just return null
          console.error('Server error when getting token:', response.status);
          return null;
        }
        return null;
      }

      const data = await response.json();
      const token = data.token || null;
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  /**
   * Set token (handled by backend setting httpOnly cookie)
   */
  async setToken(tokenInfo: TokenInfo): Promise<void> {
    try {
      await fetch(this.tokenEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tokenInfo),
      });
    } catch (error) {
      console.error('Error setting token:', error);
      throw error;
    }
  }

  /**
   * Clear the token (logout)
   */
  async clearToken(): Promise<void> {
    try {
      await fetch(this.tokenEndpoint, {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error clearing token:', error);
    }
  }

  /**
   * Check if the current token is expired
   */
  async isTokenExpired(): Promise<boolean> {
    const token = await this.getToken();
    return !token;
  }

  /**
   * Refresh the access token using the refresh token
   */
  async refreshToken(): Promise<string | null> {
    try {
      const response = await fetch(this.refreshEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Token refresh failed - let UI components handle redirect
        return null;
      }

      const data = await response.json();
      return data.token || null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Network error or other issue - let UI components handle redirect
      return null;
    }
  }

  /**
   * Redirect to Access Group login page
   */
  private redirectToLogin(): void {
    const redirectUrl = getLoginRedirectUrl();
    
    window.location.href = redirectUrl;
  }
}

/**
 * Fallback secure storage implementation for development
 * Uses secure in-memory storage with automatic cleanup
 */
export class MemoryTokenStorage implements SecureTokenStorage {
  private tokenData: TokenInfo | null = null;
  private cleanupTimer: NodeJS.Timeout | null = null;

  async getToken(): Promise<string | null> {
    if (!this.tokenData) {
      return null;
    }

    if (await this.isTokenExpired()) {
      await this.clearToken();
      return null;
    }

    return this.tokenData.token;
  }

  async setToken(tokenInfo: TokenInfo): Promise<void> {
    this.tokenData = tokenInfo;
    
    // Set up automatic cleanup when token expires
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
    }
    
    const expiresIn = tokenInfo.expiresAt - Date.now();
    if (expiresIn > 0) {
      this.cleanupTimer = setTimeout(() => {
        this.clearToken();
      }, expiresIn);
    }
  }

  async clearToken(): Promise<void> {
    this.tokenData = null;
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  async isTokenExpired(): Promise<boolean> {
    if (!this.tokenData) {
      return true;
    }
    
    return Date.now() >= this.tokenData.expiresAt;
  }

  async refreshToken(): Promise<string | null> {
    // For development, we'll just return null and require re-authentication
    await this.clearToken();
    return null;
  }
}

/**
 * Factory function to create the appropriate token storage implementation
 */
export function createTokenStorage(baseUrl: string, useSecureCookies = true): SecureTokenStorage {
  if (useSecureCookies && typeof window !== 'undefined') {
    return new CookieTokenStorage(baseUrl);
  }
  
  // Fallback for development or server-side rendering
  return new MemoryTokenStorage();
}

/**
 * Token storage configuration
 */
export const TOKEN_CONFIG = {
  // Use secure cookies in production
  useSecureCookies: process.env.NODE_ENV === 'production',
  
  // Environment-specific cookie names
  getCookieNames: () => {
    const env = process.env.NODE_ENV || 'development';
    const reactAppEnv = process.env.REACT_APP_ENV || '';
    
    // Check for development environment
    if (env === 'development' || reactAppEnv === 'dev') {
      return {
        accessToken: 'dev_AccessToken',
        refreshToken: 'dev_RefreshToken'
      };
    } 
    
    // Check for QA/staging environment
    if (reactAppEnv === 'qa' || reactAppEnv === 'staging') {
      return {
        accessToken: 'qa_AccessToken', 
        refreshToken: 'qa_RefreshToken'
      };
    }
    
    // Production environment (default)
    return {
      accessToken: 'AccessToken',
      refreshToken: 'RefreshToken'
    };
  },
  
  // Cookie configuration for backend
  cookieOptions: {
    httpOnly: true,
    secure: process.env.NODE_ENV === 'production', // HTTPS only in production
    sameSite: 'strict' as const,
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
  },
  
  // Refresh token before it expires
  refreshThreshold: 5 * 60 * 1000, // 5 minutes before expiry
} as const;
